import React, { useCallback, useEffect, useState } from "react";
import { cn } from "../../../lib/utils";
import { BlogCategoryApi } from "../services/BlogCategoryApi";

export const CategoryFilter = ({
  activeCategory,
  activeCategoryId,
  onCategoryChange,
  className,
}) => {
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchAllCategories = async () => {
      const blogCategoryApi = new BlogCategoryApi();
      const allCategories = [];

      let pageNo = 1;
      const pageSize = 10;
      let isLast = false;

      try {
        while (!isLast) {
          const data = await blogCategoryApi.getAll({ pageNo, pageSize });

          const page = data?.categories;

          if (!page || !Array.isArray(page.content)) {
            throw new Error("Invalid paginated categories response");
          }

          allCategories.push(...page.content);
          isLast = page.last === true;
          pageNo++;
        }

        setCategories(
          allCategories.map((category) => ({
            id: category.id,
            name: category.name,
            slug: category.slug,
            description: category.description,
            parentCategory: category.parentCategory,
          }))
        );
      } catch (err) {
        console.error("Failed to fetch categories:", err);
        setError("Failed to load categories.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchAllCategories();
  }, []);

  const isActive = useCallback(
    (slug) => slug === activeCategory,
    [activeCategory]
  );

  const buttonClasses = useCallback(
    (active) =>
      cn(
        "px-6 py-3 text-sm font-semibold rounded-full transition-all duration-300 transform hover:scale-105 active:scale-95",
        active
          ? "bg-gradient-to-r from-primary via-primary to-primary/90 text-white shadow-lg shadow-primary/30 hover:shadow-xl"
          : "bg-white text-gray-700 border border-gray-200 hover:bg-gray-50 hover:border-primary/20 hover:border-2 shadow-sm hover:shadow-md"
      ),
    []
  );

  /* ===========================
     LOADING STATE
     =========================== */
  if (isLoading) {
    return (
      <div className={cn("flex flex-wrap gap-3", className)}>
        {Array.from({ length: 5 }).map((_, i) => (
          <div
            key={i}
            className="h-12 w-28 rounded-full bg-gradient-to-r from-gray-200 to-gray-300 animate-pulse"
          />
        ))}
      </div>
    );
  }

  /* ===========================
     ERROR STATE
     =========================== */
  if (error) {
    return (
      <div className={cn("flex flex-wrap gap-3", className)}>
        <p className="text-red-600">{error}</p>
      </div>
    );
  }

  /* ===========================
     RENDER
     =========================== */
  return (
    <div className={cn("flex flex-wrap gap-3", className)}>
      {/* All Posts */}
      <button
        onClick={() => onCategoryChange("", "")}
        className={buttonClasses(activeCategory === "")}
      >
        All Posts
      </button>

      {/* Categories */}
      {categories.map((category) => (
        <button
          key={category.id}
          onClick={() => onCategoryChange(category.slug, category.id)}
          className={buttonClasses(isActive(category.slug))}
        >
          {category.name}
        </button>
      ))}
    </div>
  );
};
